import type { Comparison } from "../../lib/comparisons";
import { no, partial, unstated, yes } from "./cells";

export const kadolog: Comparison = {
  name: "Kadolog",
  site: "kadolog.com",
  linkLabel: "Alternative à Kadolog",
  checkedOn: "2026-09-29",
  seoTitle: "Alternative à Kadolog : Listou ou Kadolog ? Comparatif 2026 · Listou",
  seoDescription:
    "Listou est gratuit, sans limite de réservations. Kadolog est gratuit jusqu'à 10 réservations, puis Premium à 29 € par liste. Design, surprise, gestion à deux : le comparatif point par point.",
  h1: "Listou ou Kadolog : quelle liste de naissance choisir ?",
  summary:
    "Listou et Kadolog sont deux listes de cadeaux où l'on ajoute des articles de n'importe quelle boutique. Listou est gratuit, sans limite de réservations. Kadolog propose une formule gratuite, où les proches ne peuvent plus réserver au-delà de 10 réservations, puis Premium à 29 € par liste (39 € pour Extra). Côté fonctions, Listou se personnalise entièrement (modèle de page, couleurs, police, stickers), garde la surprise jusqu'à l'événement, se gère à deux et permet d'offrir anonymement. Kadolog, en ligne depuis 2008, intègre le paiement par carte pour recevoir de l'argent, avec 2,4 % de frais.",
  rows: [
    {
      feature: "Prix",
      listou: yes("Gratuit, sans limite de réservations"),
      them: partial("Gratuit à vie ; Premium 29 € par liste, Extra 39 € par liste"),
    },
    {
      feature: "Nombre de réservations",
      listou: yes("Sans limite"),
      them: partial("Sans Premium, au-delà de 10 réservations, plus de réservation possible"),
    },
    {
      feature: "Personnaliser le design de la liste",
      listou: yes("6 modèles de page, 13 palettes, 4 polices, 162 stickers, fonds à motifs, photo"),
      them: partial("Photo de couverture, titre, message d'accueil, fond d'écran"),
    },
    {
      feature: "Compte pour créer la liste",
      listou: yes("Un email ou Google, sans mot de passe"),
      them: yes("Un compte utilisateur"),
    },
    {
      feature: "Articles de n'importe quelle boutique",
      listou: yes("Coller le lien : photo et prix récupérés"),
      them: yes("Oui : Bouton magique, recherche, ou création à la main"),
    },
    {
      feature: "Visuels à partager",
      listou: yes("QR code pour le faire-part, visuels pour Instagram"),
      them: partial("Adresse de la liste à communiquer ; liste masquable, code d'accès possible"),
    },
    {
      feature: "Les parents ne voient pas ce qui est réservé",
      listou: yes("Oui, par défaut, jusqu'au lendemain de l'événement"),
      them: no("Non : l'onglet « mes cadeaux reçus » liste tous les cadeaux réservés"),
    },
    {
      feature: "Offrir sans que les parents sachent qui",
      listou: yes("Oui, même après la révélation"),
      them: unstated("Non décrit dans la FAQ"),
    },
    {
      feature: "Gérer la liste à deux",
      listou: yes("Co-propriétaire, et invités qui peuvent modifier"),
      them: unstated("Pas de co-gestion décrite dans la FAQ"),
    },
    {
      feature: "Participation en argent",
      listou: partial("Lien vers votre cagnotte (Leetchi, Lydia…), Listou ne prend aucun paiement"),
      them: yes("Virement sans frais, ou paiement par carte à 2,4 % (Premium)"),
    },
    {
      feature: "Secret Santa",
      listou: yes("Oui, avec exclusions, et des listes en groupe"),
      them: yes("Oui, tirage en ligne"),
    },
    {
      feature: "Occasions",
      listou: yes("Naissance, Noël, anniversaire, mariage"),
      them: yes("Naissance, mariage, Noël, anniversaire et autres"),
    },
  ],
  body: `
## Prix et limites

C'est la différence la plus simple à vérifier. **Listou est gratuit**, sans limite de réservations. Listou se rémunère par une commission que certaines boutiques lui versent quand un proche achète via la liste, sans changer le prix payé.

**Kadolog** affiche trois formules sur sa page « Tarifs & Conditions » :

- **Gratuit à vie**, 0 € : « Vous faites la liste de vos envies. Vos proches achètent les cadeaux et vous les apportent ou les font livrer. »
- **Premium**, « prix unique de 29 € par liste » : vos proches vous versent le montant des cadeaux, par virement ou par paiement en ligne, et vous les achetez vous-même. La page propose de tester cette option gratuitement « jusqu'à 10 cadeaux réservés ».
- **Extra**, 39 € par liste : Premium, plus des rappels de paiement aux proches via Kadolog, des remerciements personnalisés par mail et une adresse personnalisée pour la liste.

La FAQ de Kadolog précise la limite de la formule gratuite : sans Premium, « au-delà de 10 réservations de cadeaux, vos bienfaiteurs ne pourront plus réserver de cadeaux » et le créateur n'a plus accès aux cadeaux réservés, jusqu'à la souscription. Les conditions d'utilisation ajoutent que Premium est obligatoire pour qui communique ses coordonnées bancaires, de quelque manière que ce soit.

Pour une liste de naissance, qui compte souvent plus de dix cadeaux, cette limite compte : sur Listou, elle n'existe pas.

## Une liste à votre image

Sur **Listou**, tout se règle dans l'onglet Apparence, avec l'aperçu en direct :

- **6 modèles de page** : la mise en page elle-même change, pas seulement les couleurs.
- **13 palettes**, plus une couleur d'accent au choix.
- **4 polices** pour les titres.
- **162 stickers** dessinés, en 18 thèmes (naissance, Noël, mariage…), pour décorer l'en-tête ou tapisser le fond en motifs.
- **Une photo de couverture** et un emoji pour la liste.

Sur **Kadolog**, la FAQ décrit une photo de couverture (des illustrations du site, ou la vôtre), un titre, un message d'accueil et un espace pour raconter votre histoire. La page « Avantages » parle aussi d'un « joli fond d'écran ». On peut organiser les cadeaux, ajouter des descriptions, des photos et des liens.

## La surprise gardée jusqu'au bout

Sur **Listou**, le mode surprise est activé par défaut. Tant qu'il est actif, ni vous ni votre co-parent ne voyez ce qui est réservé, ni par qui. Vous recevez seulement « un cadeau vient d'être réservé ». La révélation a lieu le lendemain de la date de l'événement, ou quand vous le décidez. Si vous ouvrez le lien de votre propre liste, Listou vous montre l'aperçu que voient vos proches, sans les réservations.

Sur **Kadolog**, la FAQ explique que le créateur est prévenu dès qu'un cadeau est réservé, et que l'onglet « mes cadeaux reçus » donne « une vue complète de tous vos cadeaux réservés ». Elle ne décrit pas de réglage pour les masquer.

Si vous préférez tout voir, le mode surprise de Listou se coupe en un clic.

## Offrir anonymement

Sur Listou, la personne qui offre peut cocher « Rester anonyme » : son nom est caché à la personne qui reçoit, y compris après la révélation, qui voit « un proche ». Les autres invités ne voient jamais le prénom, seulement que le cadeau est pris.

La FAQ de Kadolog indique que l'offrant reçoit un email de confirmation, et que le créateur retrouve les détails de chaque cadeau dans « mes cadeaux reçus ». Elle ne décrit pas de moyen d'offrir sans que le créateur le sache.

## Préparer la liste à deux

Sur Listou, vous invitez le co-parent comme **co-propriétaire** : il a les mêmes droits que vous, et la surprise reste gardée pour vous deux. Vous pouvez aussi inviter la marraine ou une amie à « modifier » : elle aide à remplir la liste sans toucher au partage ni à la surprise.

La FAQ de Kadolog ne décrit pas de gestion d'une liste par plusieurs personnes.

## Ajouter des articles

Les deux services acceptent n'importe quelle boutique, et les articles créés à la main.

Sur **Listou**, on colle le lien du produit : le nom, la photo et le prix sont récupérés. Pour les idées, l'assistant propose des articles selon l'occasion, et vous pouvez reprendre ceux que d'autres parents ont ajoutés à leurs listes. Trois niveaux d'envie, « Coup de cœur », « Envie » et « Idée », disent à vos proches ce qui compte le plus.

Sur **Kadolog**, la FAQ cite trois outils : un moteur de recherche parmi les idées créées par la communauté, le « Bouton magique », qu'on glisse dans la barre de favoris pour ajouter un cadeau depuis une boutique en ligne, et des listes types pour les naissances et les mariages. Kadolog accepte aussi les services, les expériences et la seconde main.

## Partager la liste

Sur **Listou**, l'onglet Partage donne le lien, un **QR code** à imprimer sur le faire-part, et des **visuels prêts pour Instagram**. Le lien a été trop partagé ? Vous en générez un nouveau, et l'ancien cesse de fonctionner. La liste n'apparaît pas dans les moteurs de recherche.

Sur **Kadolog**, vous communiquez l'adresse de la liste à vos proches. La FAQ décrit trois réglages de confidentialité : cacher la liste, demander à ne pas être référencé par les moteurs de recherche, et ajouter un code d'accès.

## Ce que vivent vos proches

Sur Listou, les proches ouvrent le lien et réservent **sans compte** : le prénom et l'email sont facultatifs. Un cadeau réservé apparaît aussitôt comme pris pour les autres. Pour un article voulu en plusieurs exemplaires (trois bodies, deux assiettes), chacun en réserve une partie et la liste indique combien il en reste. L'adresse de livraison n'apparaît qu'après la réservation, jamais sur la liste.

Sur Kadolog, d'après la FAQ, un cadeau réservé ne peut plus être offert par quelqu'un d'autre, les proches reçoivent un email de confirmation et peuvent annuler leur réservation pendant 7 jours.

## Les participations en argent

**Listou** ne fait transiter aucun argent. Vous pouvez ajouter un lien vers votre cagnotte (Leetchi, Lydia, PayPal…) : un bloc « Participer librement » apparaît en bas de la liste. Il n'y a pas de champ pour un IBAN.

**Kadolog** en fait un de ses services, réservé à Premium. Selon sa page de tarifs et ses conditions d'utilisation, vos proches peuvent payer par virement bancaire, sans frais, ou par carte, via un partenaire de paiement (Lemon Way, puis OPP), avec « frais de 2,4 % sur les paiements par carte ». Ces mêmes conditions prévoient des frais de tenue de compte de 5 euros par mois après 12 mois d'inactivité, c'est-à-dire sans paiement en ligne reçu et sans transfert. Pour une cagnotte, la FAQ décrit une participation libre ou un montant divisé en parts de 10, 15, 20 euros.

Si vous voulez que vos proches paient par carte directement sur la liste, Kadolog le propose et Listou non.

## Noël et les fêtes à plusieurs

Les deux proposent un tirage au sort de Secret Santa en ligne. Sur Kadolog, il est présenté sur la page dédiée à la liste de Noël.

Listou va plus loin pour les fêtes de famille : un **groupe** réunit plusieurs listes derrière un seul lien, « Noël chez les Martin ». Chacun choisit son prénom, remplit sa liste et réserve sur celles des autres. Le Secret Santa s'y ajoute, avec des exclusions pour qu'un couple ne se tire pas lui-même.

## Passer de Kadolog à Listou

Il n'y a pas d'import automatique. La migration prend quelques minutes :

1. Créez votre liste sur Listou, avec la date de l'événement.
2. Pour chaque article, copiez le lien de la boutique depuis votre liste Kadolog et collez-le dans Listou. Le nom, la photo et le prix sont récupérés.
3. Invitez votre co-parent dans les réglages.
4. Envoyez le nouveau lien à vos proches, ou imprimez son QR code sur le faire-part.

Si des cadeaux ont déjà été réservés sur Kadolog, retirez-les de la nouvelle liste pour éviter les doublons. Si vos proches ont déjà versé de l'argent via Kadolog, cette partie reste sur Kadolog.
`,
  chooseThem: [
    "Vous voulez que vos proches paient directement par carte sur la liste : Kadolog l'intègre, avec 2,4 % de frais.",
    "Vous voulez recevoir de l'argent par virement bancaire, sans passer par un service de cagnotte tiers.",
    "Vous préférez un service installé de longue date : Kadolog existe depuis 2008.",
  ],
  chooseListou: [
    "Vous voulez une liste gratuite, sans limite de réservations, même avec beaucoup de cadeaux.",
    "Vous voulez une liste qui vous ressemble : modèle, couleurs, police et stickers.",
    "Vous voulez découvrir les cadeaux à la naissance, sans savoir à l'avance qui offre quoi.",
    "Vous préparez la liste à deux, chacun de son côté.",
    "Vous organisez aussi Noël en famille : listes en groupe et Secret Santa au même endroit.",
  ],
  faq: [
    [
      "Listou est-il une bonne alternative à Kadolog ?",
      "Oui, surtout si vous voulez une liste gratuite sans limite de réservations, personnalisable, avec la surprise gardée et un co-propriétaire. Kadolog est gratuit jusqu'à 10 réservations, puis Premium coûte 29 € par liste ; il intègre en revanche le paiement par carte pour recevoir de l'argent.",
    ],
    [
      "Kadolog est-il gratuit ?",
      "Kadolog propose une formule « Gratuit à vie » à 0 €. D'après sa FAQ, sans Premium, au-delà de 10 réservations de cadeaux, les proches ne peuvent plus réserver. Premium coûte 29 € par liste et Extra 39 € par liste. Listou est gratuit, sans limite de réservations.",
    ],
    [
      "Combien coûte Kadolog Premium ?",
      "29 € par liste, en paiement unique, selon la page de tarifs de Kadolog. Premium permet de recevoir l'argent des cadeaux par virement (sans frais) ou par carte (2,4 % de frais). Extra est à 39 € par liste.",
    ],
    [
      "Peut-on personnaliser le design de sa liste ?",
      "Sur Listou, oui, entièrement : 6 modèles de page, 13 palettes et une couleur d'accent, 4 polices de titre, 162 stickers en 18 thèmes, des fonds à motifs et une photo de couverture. Sur Kadolog, on choisit une photo de couverture, un titre et un message d'accueil, et la page Avantages évoque un fond d'écran.",
    ],
    [
      "Sur Kadolog, voit-on les cadeaux réservés ?",
      "D'après la FAQ de Kadolog, le créateur est prévenu à chaque réservation et l'onglet « mes cadeaux reçus » affiche tous les cadeaux réservés. Elle ne décrit pas de réglage pour les cacher. Sur Listou, le mode surprise, activé par défaut, les cache jusqu'au lendemain de l'événement.",
    ],
    [
      "Peut-on gérer une liste Kadolog à deux ?",
      "La FAQ de Kadolog ne décrit pas de gestion à plusieurs. Sur Listou, le co-parent devient co-propriétaire, avec les mêmes droits, et vous pouvez inviter d'autres personnes à modifier la liste.",
    ],
    [
      "Peut-on recevoir de l'argent plutôt que des cadeaux ?",
      "Sur Kadolog, avec Premium, vos proches paient par virement (sans frais) ou par carte (2,4 % de frais). Sur Listou, vous ajoutez un lien vers votre cagnotte (Leetchi, Lydia, PayPal…) ; Listou ne fait transiter aucun argent.",
    ],
    [
      "Peut-on passer de Kadolog à Listou ?",
      "Il n'y a pas d'import automatique. Recréez la liste sur Listou en collant les liens de vos articles : le nom, la photo et le prix sont récupérés à chaque fois. Retirez les cadeaux déjà réservés pour éviter les doublons.",
    ],
  ],
  sources: [
    {
      label: "Kadolog : tarifs et conditions",
      url: "https://www.kadolog.com/fr-fr/tarifs-et-conditions",
    },
    { label: "Kadolog : FAQ", url: "https://www.kadolog.com/fr-fr/faq" },
    {
      label: "Kadolog : FAQ des détenteurs d'une liste",
      url: "https://www.kadolog.com/fr-fr/faq/45203",
    },
    {
      label: "Kadolog : mentions légales et conditions générales d'utilisation",
      url: "https://www.kadolog.com/fr-fr/legal",
    },
    { label: "Kadolog : avantages", url: "https://www.kadolog.com/fr-fr/avantages" },
    {
      label: "Kadolog : liste de Noël et Secret Santa",
      url: "https://www.kadolog.com/fr-fr/liste-de-cadeaux-de-noel",
    },
  ],
};
